<script setup lang="ts">
/**
 * Renders a message's ordered content parts.
 *
 * The ordering is the whole point of `ContentPart[]`: "text, then image, then
 * more text" is expressible here and was not in the shape this replaces, so the
 * loop renders parts in sequence rather than grouping by kind.
 *
 * Only assistant text is parsed as markdown. User text is echoed as plain text —
 * there is no reason to parse markdown in a repeat of what the user just typed,
 * and not parsing it is one less untrusted-HTML surface.
 */
import { computed } from 'vue';

import type { ContentPart } from '../types/message';
import { renderMarkdown } from '@/lib/markdown';

const props = defineProps<{
  parts: readonly ContentPart[];
  markdown?: boolean;
}>();

const rendered = computed(() =>
  props.parts.map((part) =>
    part.type === 'text' && props.markdown
      ? { part, html: renderMarkdown(part.text) }
      : { part, html: null },
  ),
);

function dataUrl(part: Extract<ContentPart, { type: 'image' | 'audio' }>): string {
  return `data:${part.mimeType};base64,${part.data}`;
}

/**
 * Copy-to-clipboard for fenced code blocks.
 *
 * Delegated from the container because the buttons live inside markdown-rendered
 * HTML, where Vue has no components to bind to. The handler reads the block's
 * own `<code>` text — never anything reconstructed from the source — so what
 * lands on the clipboard is exactly what the reader sees.
 */
async function onCopyClick(event: MouseEvent) {
  const button = (event.target as HTMLElement | null)?.closest('[data-copy]');
  if (!(button instanceof HTMLElement)) return;
  const code = button.closest('pre')?.querySelector('code')?.textContent ?? '';
  if (!code) return;
  try {
    await navigator.clipboard.writeText(code);
    const previous = button.textContent;
    button.textContent = 'Copied';
    setTimeout(() => {
      button.textContent = previous;
    }, 1200);
  } catch {
    // Clipboard denied (insecure context / permissions) — leave the label alone
    // rather than claiming a copy that did not happen.
  }
}
</script>

<template>
  <div data-testid="content-parts" class="space-y-2">
    <template v-for="({ part, html }, i) in rendered" :key="i">
      <!-- `md`, not `prose`: the typography plugin is not installed, so the prose
           classes this used to carry generated no CSS at all and markdown rendered
           as flat unstyled text. Styles live in src/style.css. -->
      <!-- eslint-disable-next-line vue/no-v-html -- renderMarkdown sanitizes; see markdown.ts -->
      <div
        v-if="part.type === 'text' && html !== null"
        class="md max-w-none break-words"
        v-html="html"
        @click="onCopyClick"
      />
      <p
        v-else-if="part.type === 'text'"
        class="whitespace-pre-wrap break-words text-sm"
        data-testid="content-text"
      >
        {{ part.text }}
      </p>

      <figure v-else-if="part.type === 'image'" data-testid="content-image">
        <img
          :src="dataUrl(part)"
          :alt="part.filename ?? 'attached image'"
          class="max-h-80 rounded-lg border border-gray-200 dark:border-gray-700"
          loading="lazy"
        />
        <figcaption v-if="part.filename" class="mt-1 text-xs text-gray-400">
          {{ part.filename }}
        </figcaption>
      </figure>

      <figure v-else-if="part.type === 'audio'" data-testid="content-audio">
        <audio :src="dataUrl(part)" controls class="w-full max-w-sm" />
        <figcaption v-if="part.filename" class="mt-1 text-xs text-gray-400">
          {{ part.filename }}
        </figcaption>
      </figure>
    </template>
  </div>
</template>
